import classNames from 'classnames';
import { ReactNode, useEffect, useRef } from 'react';
import { DropdownMenuOption, DropdownMenuSectionOption, DropdownMenuTextOption } from '../../types';
import DropdownMenuItem from './DropdownMenuItem';
import styles from './styles/styles.module.scss';

const isDropdownMenuTextOption = (option: DropdownMenuOption): option is DropdownMenuTextOption => 'title' in option;
const isDropdownMenuSectionOption = (option: DropdownMenuOption): option is DropdownMenuSectionOption => 'sectionHeader' in option;

type DropdownMenuItemsProps = {
  className?: string;
  children?: ReactNode;
  options: DropdownMenuOption[];
  scrollSelectedIntoView?: boolean;
  textVariant?: 'text100' | 'text200';
};

const DropdownMenuItems = ({ className, children, options, scrollSelectedIntoView, textVariant = 'text100' }: DropdownMenuItemsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedIndex = options.findIndex(option => isDropdownMenuTextOption(option) && option.selected);

  useEffect(() => {
    const container = containerRef.current;
    const selectedItem = container?.children[selectedIndex];
    if (!scrollSelectedIntoView || !container || !(selectedItem instanceof HTMLElement)) {
      return;
    }
    container.scrollTop = selectedItem.offsetTop - container.offsetTop - (container.clientHeight - selectedItem.offsetHeight) / 2;
    // Only run on mount, since the menu is unmounted while it is closed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classNames(styles.container, className)} ref={containerRef}>
      {options.map((option, index) => {
        const key = `option-${index}`;
        if (isDropdownMenuTextOption(option)) {
          return <DropdownMenuItem key={key} option={option} textVariant={textVariant} />;
        }
        if (isDropdownMenuSectionOption(option)) {
          return (
            <div key={key} className={styles.sectionOption}>
              <div key={key} className={styles.sectionDivider} />
              {option.sectionHeader ? <span className={styles.sectionHeader}>{option.sectionHeader}</span> : null}
            </div>
          );
        }
        return option;
      })}
      {children}
    </div>
  );
};

export default DropdownMenuItems;
